import React from 'react';
import { Box, Typography } from '@mui/material';
import { brand } from '../theme';
import type { TeamMember as TeamMemberData } from '../data/team';
import { useAge } from '../hooks/useAge';
import ResponsiveImage from './ResponsiveImage';

interface TeamMemberProps {
  member: TeamMemberData;
  /** Mirror the layout so consecutive members alternate sides on desktop. */
  reverse?: boolean;
}

/** A single team member: circular photo + name, role and bio. Data-driven. */
const TeamMember: React.FC<TeamMemberProps> = ({ member, reverse = false }) => {
  const age = useAge(member.birthDate);
  const description = member.description.replace('{age}', String(age));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: reverse ? 'row-reverse' : 'row' },
        alignItems: 'center',
        gap: { xs: 2, md: 4 },
        mb: { xs: 5, md: 6 },
      }}
    >
      <Box sx={{ flexShrink: 0, width: { xs: 140, md: 170 } }}>
        <ResponsiveImage
          src={member.img}
          alt={member.name}
          aspectRatio="1 / 1"
          sizes="170px"
          sx={{ borderRadius: '50%', boxShadow: 3 }}
        />
      </Box>

      <Box sx={{ maxWidth: 680, textAlign: { xs: 'center', md: 'left' } }}>
        <Typography variant="h6" sx={{ color: brand.orange }}>
          {member.name}
        </Typography>
        <Typography variant="subtitle2" sx={{ color: brand.orange, opacity: 0.85, mb: 1 }}>
          {member.role}
        </Typography>
        <Typography variant="body1" sx={{ color: 'common.white', textAlign: { xs: 'center', md: 'justify' } }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

export default TeamMember;
